import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    json.http_status = res.status;
    return json;
  } catch {
    return { status_code: '50000', message: 'Request failed' };
  }
};

const isAuthError = (res) => {
  const code = String(res?.statusCode || res?.status_code || '');
  const message = String(res?.message || '').toLowerCase();
  if (code === '10003') return true;
  return (
    code === '10001' &&
    (message.includes('token') ||
      message.includes('jwt') ||
      message.includes('credential') ||
      message.includes('authenticated'))
  );
};

const authFetchWithRetry = async (path, options = {}) => {
  let res = await authFetch(path, options);
  if (isAuthError(res)) {
    await new Promise((resolve) => setTimeout(resolve, 800));
    res = await authFetch(path, options);
  }
  return res;
};

export const isPromoApiSuccess = (res) =>
  String(res?.status_code || res?.statusCode || '') === '10000';

export const getPromoPayload = (res) => res?.data ?? null;

export const getPromoErrorMessage = (res, fallback) => {
  if (isAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }
  const details = (Array.isArray(res?.data?.errors) ? res.data.errors : [])
    .map((error) => [error?.path, error?.message].filter(Boolean).join(': '))
    .filter(Boolean)
    .join(' ');
  const message = String(res?.message || '');
  if (details) return message ? `${message} ${details}` : details;
  return message || fallback;
};

const templatesPath = (surfaceKey) => `/admin/promo/surfaces/${surfaceKey}/templates`;

/** GET /admin/promo/surfaces — hub. include=templates nests each slot's library page. */
export const listPromoSurfaces = async ({ include, keys, q, status, limit, offset, cursor } = {}) => {
  const params = new URLSearchParams();
  if (include) params.set('include', include);
  if (keys) params.set('keys', Array.isArray(keys) ? keys.join(',') : String(keys));
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  if (limit != null) params.set('limit', String(limit));
  if (cursor) params.set('cursor', String(cursor));
  else if (offset != null) params.set('offset', String(offset));
  const query = params.toString();
  return authFetchWithRetry(`/admin/promo/surfaces${query ? `?${query}` : ''}`, { method: 'GET' });
};

/** PATCH /admin/promo/surfaces/:surfaceKey — On/Off. Off does not change the active template. */
export const setPromoSurfaceEnabled = async (surfaceKey, enabled) =>
  authFetch(`/admin/promo/surfaces/${surfaceKey}`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled: Boolean(enabled) }),
  });

/** PUT /admin/promo/surfaces/:surfaceKey/active-template — previous active becomes draft. */
export const setPromoActiveTemplate = async (surfaceKey, templateId) =>
  authFetch(`/admin/promo/surfaces/${surfaceKey}/active-template`, {
    method: 'PUT',
    body: JSON.stringify({ template_id: templateId }),
  });

/** DELETE /admin/promo/surfaces/:surfaceKey/active-template — active becomes draft. Switch stays. */
export const clearPromoActiveTemplate = async (surfaceKey) =>
  authFetch(`/admin/promo/surfaces/${surfaceKey}/active-template`, { method: 'DELETE' });

/** GET /admin/promo/surfaces/:surfaceKey/templates — alias of that slot's library page. */
export const listPromoTemplates = async (surfaceKey, { limit = 20, offset = 0, status, q, cursor } = {}) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', String(cursor));
  else params.set('offset', String(offset));
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  return authFetchWithRetry(`${templatesPath(surfaceKey)}?${params.toString()}`, { method: 'GET' });
};

/** POST /admin/promo/surfaces/:surfaceKey/templates — active when the creative is eligible, unless status is draft. */
export const createPromoTemplate = async (surfaceKey, body) =>
  authFetch(`${templatesPath(surfaceKey)}`, { method: 'POST', body: JSON.stringify(body) });

/** GET /admin/promo/surfaces/:surfaceKey/templates/:id */
export const getPromoTemplate = async (surfaceKey, id) =>
  authFetchWithRetry(`${templatesPath(surfaceKey)}/${id}`, { method: 'GET' });

/** PUT /admin/promo/surfaces/:surfaceKey/templates/:id — full replace of editable fields */
export const updatePromoTemplate = async (surfaceKey, id, body) =>
  authFetch(`${templatesPath(surfaceKey)}/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** DELETE /admin/promo/surfaces/:surfaceKey/templates/:id — allowed while active. */
export const deletePromoTemplate = async (surfaceKey, id) =>
  authFetch(`${templatesPath(surfaceKey)}/${id}`, { method: 'DELETE' });

/** PATCH /admin/promo/surfaces/:surfaceKey/templates/:id/activate */
export const activatePromoTemplate = async (surfaceKey, id) =>
  authFetch(`${templatesPath(surfaceKey)}/${id}/activate`, { method: 'PATCH', body: '{}' });

/** PATCH /admin/promo/surfaces/:surfaceKey/deactivate */
export const deactivatePromoSurface = async (surfaceKey) =>
  authFetch(`/admin/promo/surfaces/${surfaceKey}/deactivate`, { method: 'PATCH', body: '{}' });

/** GET /admin/promo/surfaces/live-preview — the app data object. Optional app_version filters like the app. */
export const getPromoLivePreview = async (appVersion) => {
  const params = new URLSearchParams();
  if (appVersion) params.set('app_version', appVersion);
  const query = params.toString();
  return authFetchWithRetry(`/admin/promo/surfaces/live-preview${query ? `?${query}` : ''}`, { method: 'GET' });
};

/** POST /admin/promo/surfaces/:surfaceKey/templates/:id/ingest */
export const startPromoIngest = async (surfaceKey, id, body) =>
  authFetch(`${templatesPath(surfaceKey)}/${id}/ingest`, { method: 'POST', body: JSON.stringify(body) });

/** POST /admin/promo/ingest/:ingestId/complete */
export const completePromoIngest = async (ingestId) =>
  authFetch(`/admin/promo/ingest/${ingestId}/complete`, { method: 'POST', body: '{}' });

/** GET /admin/promo/ingest/:ingestId */
export const getPromoIngest = async (ingestId) =>
  authFetchWithRetry(`/admin/promo/ingest/${ingestId}`, { method: 'GET' });

/** PUT the MP4 to the presigned URL. This request does not go to the API. */
export const uploadPromoMp4 = async (uploadUrl, file) => {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'video/mp4' },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`The file upload failed (${res.status}).`);
  }
};
